// Application status and driver standing, both always shown and editable — plus
// driver type, which moved here from the Account tab's Identity card.
//
// On the standalone driver profile the standing pill lives in the page header, because
// standing IS that page's frame. Here it cannot: this page's header is shared by every
// tab, and a driver-standing control in it would headline a person who may be mostly a
// rider with their driver standing. So status becomes the first section of the Driver
// tab, above the requirements it frames.
//
// Showing BOTH rows is the point. A single row that silently swapped vocabulary at the
// latch would leave a reviewer unable to tell whether `awaiting_docs` meant "never
// finished applying" or "working driver whose insurance lapsed" — which is the exact
// ambiguity phaseStatus exists to prevent. Application status locks only while its
// current value is approved; standing remains independently editable.
import React from 'react';
import { Box, Stack, Typography } from '../../ui/core';
import { hairline } from '../../ui/styles';
import { CheckCircleIcon } from '../../ui/icons';
import { SectionCard, StandingPill } from '../DriverProfile/components';
import DriverTypeField from '../../components/widgets/DriversApproval/DriverTypeField';

/**
 * Label, the editable pill, and the sentence explaining what editing it does.
 *
 * Stacks below sm. Three things in one row leaves the `why` sentence about 50px on a
 * phone — a column of single words next to a control whose consequences it is supposed
 * to explain. Stacked, the label and pill keep their row and the sentence gets the full
 * width underneath, which is the order it is read in anyway.
 */
const StatusRow = ({ label, why, first, disabled = false, ...pill }) => (
  <Box
    sx={{
      px: 1.75,
      py: 1.35,
      borderTop: first ? 'none' : (t) => `1px solid ${hairline(t)}`,
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: 1.5,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Box sx={{ width: { xs: 72, sm: 96 }, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{label}</Typography>
      </Box>
      <StandingPill {...pill} busy={pill.busy || disabled} allowAll />
    </Stack>
    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', flex: 1, minWidth: 0 }}>{why}</Typography>
  </Box>
);

const DriverStatusCard = ({ profile, user, onApplicationAction, onStandingAction, onStandingReevaluate, busy }) => {
  const approved = profile.applicationStatus === 'approved';
  const shared = {
    standing: profile.currentStanding,
    applicationStatus: profile.applicationStatus,
    unresolved: profile.blockingCount,
    busy,
  };
  return (
    <SectionCard
      title="Driver status"
      icon={<CheckCircleIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        <>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          {/* Wraps below sm. This is a whole sentence rather than the short counts the
              other sections put here, so `nowrap` ran it straight off the end of a card
              that clips — on a phone the gloss was cut mid-word. */}
          <Typography
            sx={{
              fontSize: '11.5px',
              color: 'text.disabled',
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              textAlign: { xs: 'right', sm: 'left' },
              minWidth: 0,
            }}
          >
            {approved ? 'approved — standing owns this driver' : 'application status is editable'}
          </Typography>
        </>
      }
    >
      <StatusRow
        first
        label="Application"
        hasBeenApproved={false}
        onAction={onApplicationAction}
        reasonEnabled={false}
        disabled={approved}
        why={
          approved
            ? 'Locked while approved. Requests for information, review, suspension, and deactivation belong to standing.'
            : 'Editable because application status is not approved. Application changes do not change standing.'
        }
        {...shared}
      />
      <StatusRow
        label="Standing"
        hasBeenApproved
        onAction={onStandingAction}
        onReevaluate={onStandingReevaluate}
        why="Editable directly. Changing standing does not change application status."
        {...shared}
      />
      {/* Driver type, moved off the Identity card on the Account tab. It was filed with
          the phone number and the email, but it is not a fact about the person — it is a
          write to the DRIVER record, and it belongs beside the other two things about
          this driver that an admin changes. Same control, same in-place menu; only its
          address changed. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 1.75, py: 0.6, borderTop: (t) => `1px solid ${hairline(t)}`, minWidth: 0 }}
      >
        <Box sx={{ width: { xs: 72, sm: 96 }, flexShrink: 0 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Type</Typography>
        </Box>
        {/* The user document, not `profile` — `editDriver` reads the driver id off the
            object it is handed, and this is the same object the Identity card passed. */}
        <DriverTypeField driver={user} />
      </Stack>
    </SectionCard>
  );
};

export default DriverStatusCard;
